import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronUp,
  ImagePlus,
  Loader2,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { supabase } from '../../../../Supabase/SupabaseClient';
import { convertToWebp, formatBytes, uploadImageToBucket } from '../../../../utils/imageUpload';
import styles from './InspiracionAdmin.module.css';

const BUCKET = 'inspiracion';
const TABLA = 'imagenes_inspiracion';
const MAX_EDGE = 2000;
const ACCEPT = 'image/jpeg,image/png,image/webp,image/avif';

const PLACEHOLDER =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="800" height="600" viewBox="0 0 800 600"><rect width="100%" height="100%" fill="#f3f4f6"/><text x="50%" y="50%" fill="#9ca3af" font-family="Arial, sans-serif" font-size="20" dominant-baseline="middle" text-anchor="middle">Sin foto</text></svg>`
  );

let secuencia = 0;

function tituloPorDefecto(file) {
  return file.name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ').trim() || 'Sin título';
}

function InspiracionAdmin({ standalone = false }) {
  const [estilos, setEstilos] = useState([]);
  const [estiloId, setEstiloId] = useState('');
  const [modo, setModo] = useState('subir');

  const [cola, setCola] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [subiendo, setSubiendo] = useState(false);
  const [progreso, setProgreso] = useState({ hechos: 0, total: 0 });

  const [imagenes, setImagenes] = useState([]);
  const [cargandoImagenes, setCargandoImagenes] = useState(false);
  const [ocupadoId, setOcupadoId] = useState(null);

  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [resumen, setResumen] = useState(null);

  const inputRef = useRef(null);
  const previewsRef = useRef([]);

  const registrarPreview = useCallback((file) => {
    const url = URL.createObjectURL(file);
    previewsRef.current.push(url);
    return url;
  }, []);

  useEffect(
    () => () => {
      previewsRef.current.forEach((url) => URL.revokeObjectURL(url));
      previewsRef.current = [];
    },
    []
  );

  const cargarEstilos = useCallback(async () => {
    try {
      const { data, error: loadError } = await supabase
        .from('estilos_fotograficos')
        .select('id, nombre')
        .order('nombre');

      if (loadError) throw loadError;
      setEstilos(data || []);
    } catch (err) {
      setError(err.message || 'No se pudieron cargar los estilos fotográficos.');
    }
  }, []);

  const cargarImagenes = useCallback(async () => {
    if (!estiloId) {
      setImagenes([]);
      return;
    }

    setCargandoImagenes(true);
    try {
      const { data, error: loadError } = await supabase
        .from(TABLA)
        .select('id, estilo_id, titulo, creditos, ruta_imagen, url_publica, orden')
        .eq('estilo_id', estiloId)
        .order('orden', { ascending: true });

      if (loadError) throw loadError;
      setImagenes(data || []);
    } catch (err) {
      setError(err.message || 'No se pudieron cargar las imágenes del estilo.');
    } finally {
      setCargandoImagenes(false);
    }
  }, [estiloId]);

  useEffect(() => {
    cargarEstilos();
  }, [cargarEstilos]);

  useEffect(() => {
    if (modo === 'administrar') {
      cargarImagenes();
    }
  }, [modo, cargarImagenes]);

  const estiloNombre = useMemo(
    () => estilos.find((e) => e.id === estiloId)?.nombre || '',
    [estilos, estiloId]
  );

  const limpiarCola = useCallback(() => {
    setCola((prev) => {
      prev.forEach((item) => {
        const idx = previewsRef.current.indexOf(item.previewUrl);
        if (idx !== -1) {
          URL.revokeObjectURL(item.previewUrl);
          previewsRef.current.splice(idx, 1);
        }
      });
      return [];
    });
  }, []);

  const agregarArchivos = useCallback(
    (fileList) => {
      const entrantes = Array.from(fileList || []).filter((f) => f.type.startsWith('image/'));
      if (!entrantes.length) {
        setError('Elegí al menos una imagen (JPG, PNG, WebP o AVIF).');
        return;
      }

      setError(null);
      setCola((prev) => [
        ...prev,
        ...entrantes.map((file) => {
          secuencia += 1;
          return {
            id: `${Date.now()}-${secuencia}`,
            file,
            previewUrl: registrarPreview(file),
            titulo: tituloPorDefecto(file),
            creditos: '',
          };
        }),
      ]);
    },
    [registrarPreview]
  );

  const quitarDeCola = (id) => {
    setCola((prev) => {
      const item = prev.find((p) => p.id === id);
      if (item) {
        URL.revokeObjectURL(item.previewUrl);
        const idx = previewsRef.current.indexOf(item.previewUrl);
        if (idx !== -1) previewsRef.current.splice(idx, 1);
      }
      return prev.filter((p) => p.id !== id);
    });
  };

  const actualizarCola = (id, campo, valor) => {
    setCola((prev) => prev.map((p) => (p.id === id ? { ...p, [campo]: valor } : p)));
  };

  const subir = async () => {
    if (!estiloId) {
      setError('Elegí un estilo fotográfico antes de subir.');
      return;
    }
    if (!cola.length) {
      setError('Agregá al menos una imagen.');
      return;
    }

    const sinTitulo = cola.find((p) => !p.titulo.trim());
    if (sinTitulo) {
      setError('Todas las imágenes necesitan un título.');
      return;
    }

    setSubiendo(true);
    setError(null);
    setSuccess(null);
    setResumen(null);
    setProgreso({ hechos: 0, total: cola.length });

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      setError('Necesitás iniciar sesión como administrador para subir imágenes.');
      setSubiendo(false);
      return;
    }

    const rutas = [];
    const antes = [];
    const despues = [];
    let ultimoOrden = 0;

    try {
      const { data: ordenData, error: ordenError } = await supabase
        .from(TABLA)
        .select('orden')
        .eq('estilo_id', estiloId)
        .order('orden', { ascending: false })
        .limit(1);

      if (ordenError) throw ordenError;
      if (ordenData && ordenData.length) ultimoOrden = ordenData[0].orden || 0;

      for (let i = 0; i < cola.length; i += 1) {
        const item = cola[i];
        const converted = await convertToWebp(item.file, { maxEdge: MAX_EDGE });
        const fileName = `${Date.now()}-${i}-${converted.fileName}`;
        const path = `estilos/${estiloId}/${fileName}`;

        const url = await uploadImageToBucket(BUCKET, path, converted.blob);
        rutas.push(path);
        antes.push(converted.originalBytes);
        despues.push(converted.bytes);

        const { error: dbError } = await supabase.from(TABLA).insert([
          {
            estilo_id: estiloId,
            titulo: item.titulo.trim(),
            creditos: item.creditos.trim() || 'Fotógrafo desconocido',
            ruta_imagen: path,
            url_publica: url,
            orden: ultimoOrden + i + 1,
          },
        ]);

        if (dbError) throw dbError;

        setProgreso({ hechos: i + 1, total: cola.length });
      }

      limpiarCola();
      setSuccess(
        `${cola.length} ${cola.length === 1 ? 'imagen subida' : 'imágenes subidas'} en WebP.`
      );
      setResumen({
        before: antes.reduce((sum, n) => sum + n, 0),
        after: despues.reduce((sum, n) => sum + n, 0),
      });

      if (modo === 'administrar') await cargarImagenes();
    } catch (err) {
      if (rutas.length) {
        await Promise.allSettled(rutas.map((p) => supabase.storage.from(BUCKET).remove([p])));
      }
      setError(err.message || 'No se pudieron subir las imágenes.');
    } finally {
      setSubiendo(false);
      setProgreso({ hechos: 0, total: 0 });
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const eliminar = async (imagen) => {
    const confirmado = window.confirm(
      `¿Eliminar "${imagen.titulo}"? Se borra el archivo del bucket y desaparece de la galería.`
    );
    if (!confirmado) return;

    setOcupadoId(imagen.id);
    setError(null);
    setSuccess(null);

    try {
      if (imagen.ruta_imagen) {
        const { error: storageError } = await supabase.storage
          .from(BUCKET)
          .remove([imagen.ruta_imagen]);
        if (storageError) console.warn('No se pudo borrar del storage:', storageError);
      }

      const { error: dbError } = await supabase.from(TABLA).delete().eq('id', imagen.id);
      if (dbError) throw dbError;

      setImagenes((prev) => prev.filter((img) => img.id !== imagen.id));
      setSuccess(`"${imagen.titulo}" se eliminó.`);
    } catch (err) {
      setError(err.message || 'No se pudo eliminar la imagen.');
    } finally {
      setOcupadoId(null);
    }
  };

  const mover = async (indice, delta) => {
    const destino = indice + delta;
    if (destino < 0 || destino >= imagenes.length) return;

    const actual = imagenes[indice];
    const vecino = imagenes[destino];
    if (!actual || !vecino) return;

    setOcupadoId(actual.id);
    setError(null);

    try {
      const { error: firstError } = await supabase
        .from(TABLA)
        .update({ orden: vecino.orden })
        .eq('id', actual.id);
      if (firstError) throw firstError;

      const { error: secondError } = await supabase
        .from(TABLA)
        .update({ orden: actual.orden })
        .eq('id', vecino.id);
      if (secondError) throw secondError;

      const copia = [...imagenes];
      copia[indice] = vecino;
      copia[destino] = actual;
      setImagenes(copia);
    } catch (err) {
      setError(err.message || 'No se pudo cambiar el orden.');
      await cargarImagenes();
    } finally {
      setOcupadoId(null);
    }
  };

  const guardarTexto = async (imagen, campo, valor) => {
    if ((imagen[campo] || '') === valor) return;

    setOcupadoId(imagen.id);
    try {
      const { error: updateError } = await supabase
        .from(TABLA)
        .update({ [campo]: valor })
        .eq('id', imagen.id);
      if (updateError) throw updateError;

      setImagenes((prev) =>
        prev.map((img) => (img.id === imagen.id ? { ...img, [campo]: valor } : img))
      );
    } catch (err) {
      setError(err.message || 'No se pudo guardar el cambio.');
    } finally {
      setOcupadoId(null);
    }
  };

  return (
    <div className={standalone ? styles.pagina : styles.wrap}>
      <div className={styles.overview}>
        Las imágenes se guardan en el bucket <strong>{BUCKET}</strong> y se convierten a{' '}
        <strong>WebP</strong> automáticamente (lado mayor a {MAX_EDGE} px) antes de subirlas.
      </div>

      <div className={styles.selectorRow}>
        <div className={styles.selectorField}>
          <label className={styles.label} htmlFor="estilo-inspiracion">
            Estilo fotográfico
          </label>
          <select
            id="estilo-inspiracion"
            className={styles.select}
            value={estiloId}
            onChange={(e) => setEstiloId(e.target.value)}
          >
            <option value="">Selecciona un estilo</option>
            {estilos.map((estilo) => (
              <option key={estilo.id} value={estilo.id}>
                {estilo.nombre}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.modeSwitch} role="tablist" aria-label="Modo">
          <button
            type="button"
            role="tab"
            aria-selected={modo === 'subir'}
            className={`${styles.modeButton} ${modo === 'subir' ? styles.modeButtonOn : ''}`}
            onClick={() => setModo('subir')}
          >
            <Upload size={15} aria-hidden="true" />
            Subir
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={modo === 'administrar'}
            className={`${styles.modeButton} ${modo === 'administrar' ? styles.modeButtonOn : ''}`}
            onClick={() => setModo('administrar')}
          >
            <ImagePlus size={15} aria-hidden="true" />
            Administrar
          </button>
        </div>
      </div>

      {error && (
        <div className={styles.errorBox} role="alert">
          <AlertTriangle size={18} aria-hidden="true" />
          <p>{error}</p>
        </div>
      )}

      {success && (
        <div className={styles.successBox} role="status">
          <Check size={18} aria-hidden="true" />
          <div>
            <p>{success}</p>
            {resumen && (
              <p className={styles.summary}>
                {formatBytes(resumen.before)} → {formatBytes(resumen.after)} de peso
              </p>
            )}
          </div>
        </div>
      )}

      {modo === 'subir' && (
        <>
          <div
            className={`${styles.dropzone} ${dragging ? styles.dropzoneOn : ''}`}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              if (!subiendo) agregarArchivos(e.dataTransfer.files);
            }}
          >
            <ImagePlus size={26} aria-hidden="true" />
            <p className={styles.dropzoneTitle}>Arrastrá las imágenes acá</p>
            <p className={styles.dropzoneHint}>o elegilas desde la computadora</p>
            <input
              ref={inputRef}
              className={styles.fileInput}
              type="file"
              accept={ACCEPT}
              multiple
              onChange={(e) => agregarArchivos(e.target.files)}
              disabled={subiendo}
            />
            <button
              type="button"
              className={styles.pickButton}
              onClick={() => inputRef.current?.click()}
              disabled={subiendo}
            >
              Elegir archivos
            </button>
          </div>

          {cola.length > 0 && (
            <section className={styles.grupo}>
              <header className={styles.grupoHeader}>
                <h2 className={styles.grupoTitulo}>Para subir</h2>
                <span className={styles.grupoContador}>{cola.length}</span>
              </header>

              <ul className={styles.queue}>
                {cola.map((item) => (
                  <li key={item.id} className={styles.queueItem}>
                    <img src={item.previewUrl} alt="" className={styles.queueThumb} />

                    <div className={styles.queueFields}>
                      <input
                        className={styles.input}
                        type="text"
                        value={item.titulo}
                        onChange={(e) => actualizarCola(item.id, 'titulo', e.target.value)}
                        placeholder="Título de la imagen"
                        aria-label="Título de la imagen"
                        disabled={subiendo}
                      />
                      <input
                        className={styles.input}
                        type="text"
                        value={item.creditos}
                        onChange={(e) => actualizarCola(item.id, 'creditos', e.target.value)}
                        placeholder="Créditos / fotógrafo"
                        aria-label="Créditos o fotógrafo"
                        disabled={subiendo}
                      />
                      <span className={styles.queueMeta}>{formatBytes(item.file.size)}</span>
                    </div>

                    <button
                      type="button"
                      className={styles.iconDanger}
                      onClick={() => quitarDeCola(item.id)}
                      disabled={subiendo}
                    >
                      <X size={16} aria-hidden="true" />
                      <span className={styles.srOnly}>Quitar de la lista</span>
                    </button>
                  </li>
                ))}
              </ul>

              <div className={styles.actions}>
                <button
                  type="button"
                  className={styles.primaryButton}
                  onClick={subir}
                  disabled={subiendo || !estiloId}
                >
                  {subiendo ? (
                    <Loader2 className={styles.spinner} size={15} aria-hidden="true" />
                  ) : (
                    <Upload size={15} aria-hidden="true" />
                  )}
                  {subiendo
                    ? `Subiendo ${progreso.hechos}/${progreso.total}…`
                    : `Subir ${cola.length} ${cola.length === 1 ? 'imagen' : 'imágenes'}`}
                </button>
                <button
                  type="button"
                  className={styles.ghostButton}
                  onClick={limpiarCola}
                  disabled={subiendo}
                >
                  Limpiar
                </button>
              </div>
            </section>
          )}
        </>
      )}

      {modo === 'administrar' && (
        <section className={styles.grupo}>
          <header className={styles.grupoHeader}>
            <h2 className={styles.grupoTitulo}>
              {estiloNombre ? `Imágenes de ${estiloNombre}` : 'Imágenes'}
            </h2>
            <span className={styles.grupoContador}>{imagenes.length}</span>
          </header>

          {!estiloId ? (
            <p className={styles.empty}>Elegí un estilo para ver sus imágenes.</p>
          ) : cargandoImagenes ? (
            <p className={styles.empty}>
              <Loader2 className={styles.spinner} size={16} aria-hidden="true" /> Cargando imágenes…
            </p>
          ) : imagenes.length === 0 ? (
            <p className={styles.empty}>Este estilo todavía no tiene imágenes.</p>
          ) : (
            <ul className={styles.grid}>
              {imagenes.map((imagen, index) => {
                const busy = ocupadoId === imagen.id;
                return (
                  <li key={imagen.id} className={styles.card}>
                    <div className={styles.preview}>
                      <img
                        src={imagen.url_publica || PLACEHOLDER}
                        alt=""
                        loading="lazy"
                        onError={(e) => {
                          if (e.currentTarget.src !== PLACEHOLDER) {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = PLACEHOLDER;
                          }
                        }}
                      />
                      <span className={styles.badgeOrden}>#{imagen.orden}</span>
                      {busy && (
                        <span className={styles.badgeCargando}>
                          <Loader2 className={styles.spinner} size={13} aria-hidden="true" />
                        </span>
                      )}
                    </div>

                    <div className={styles.cardBody}>
                      <input
                        className={styles.input}
                        type="text"
                        defaultValue={imagen.titulo || ''}
                        onBlur={(e) => guardarTexto(imagen, 'titulo', e.target.value.trim())}
                        aria-label="Título"
                        disabled={busy}
                      />
                      <input
                        className={styles.input}
                        type="text"
                        defaultValue={imagen.creditos || ''}
                        onBlur={(e) => guardarTexto(imagen, 'creditos', e.target.value.trim())}
                        aria-label="Créditos"
                        disabled={busy}
                      />

                      <div className={styles.cardActions}>
                        <button
                          type="button"
                          className={styles.iconButton}
                          onClick={() => mover(index, -1)}
                          disabled={busy || index === 0}
                        >
                          <ChevronUp size={15} aria-hidden="true" />
                          <span className={styles.srOnly}>Subir</span>
                        </button>
                        <button
                          type="button"
                          className={styles.iconButton}
                          onClick={() => mover(index, 1)}
                          disabled={busy || index === imagenes.length - 1}
                        >
                          <ChevronDown size={15} aria-hidden="true" />
                          <span className={styles.srOnly}>Bajar</span>
                        </button>
                        <button
                          type="button"
                          className={styles.iconDangerWide}
                          onClick={() => eliminar(imagen)}
                          disabled={busy}
                        >
                          <Trash2 size={15} aria-hidden="true" />
                          Eliminar
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}

export default InspiracionAdmin;